// packages/web-ui/src/components/inline-cards/MiniReactFlowPreview.tsx
//
// Read-only React Flow preview embedded inside an inline proposal card.
// Takes a `ReactFlowLayoutJson` (server-synthesised; see
// `packages/server/src/response-view/auto-view-layout-synthesizer.ts`) and
// renders it as a constrained, non-interactive React Flow canvas: no pan,
// no zoom, no drag, no controls — just the diagram and a title bar.
//
// This is the canvas the user sees BEFORE clicking Keep & Open. The full
// interactive renderer (with drag/edit/save) lives in
// `packages/web-ui/src/panels/views/ViewRenderer.tsx`; this mini variant
// reuses the same node/edge types + `buildReactFlowGraph` + `runElkLayout`
// so a kept view renders identically to its preview.

import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import {
  Background,
  ReactFlow,
  ReactFlowProvider,
  useNodesInitialized,
  useReactFlow,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type {
  ReactFlowLayoutJson,
  LayoutIntent,
} from '@tessa-lean/base/api-schemas/index.js';
import { PartNode } from '../../panels/views/PartNode.js';
import { PortNode } from '../../panels/views/PortNode.js';
import { InterfaceNode } from '../../panels/views/InterfaceNode.js';
import { ConnectionEdge } from '../../panels/views/ConnectionEdge.js';
import { EdgeLegend } from '../../panels/views/EdgeLegend.js';
import {
  buildReactFlowGraph,
  needsFirstRenderLayout,
} from '../../panels/views/layout-builder.js';
import { runLayoutWithIntent } from '../../panels/views/layout-with-intent.js';
import '../../panels/views/view-renderer.css';

const NODE_TYPES = {
  part: PartNode,
  port: PortNode,
  interface: InterfaceNode,
};
const EDGE_TYPES = {
  connection: ConnectionEdge,
};

type LoadState =
  | { kind: 'laying-out' }
  | { kind: 'ready'; nodes: Node[]; edges: Edge[] }
  | { kind: 'error'; message: string };

export interface MiniReactFlowPreviewProps {
  layout: ReactFlowLayoutJson;
  /**
   * Width of the preview pane in CSS pixels. Default 560 — matches the
   * outer card's existing `style={{ width: 560 }}` budget so the swap
   * doesn't reflow the chat message bubble.
   */
  width?: number;
  /**
   * Height of the preview pane in CSS pixels. Default 320 — same as the
   * legacy SVG preview's `maxHeight` ceiling.
   */
  height?: number;
}

/**
 * Render a non-interactive React Flow preview of a ReactFlow layout JSON.
 * Runs ELK layout once on mount to position nodes (the synthesiser emits
 * a position-free graph). All interactions are disabled.
 */
export function MiniReactFlowPreview(
  props: MiniReactFlowPreviewProps,
): JSX.Element {
  const { layout } = props;
  const width = props.width ?? 560;
  const height = props.height ?? 320;

  // LV3 fix — honour the persisted `layoutIntent` (direction / algorithm /
  // spacing / pins / groups / emphasis) so a chat-driven layout adjustment
  // ("move the radiator to the top") actually moves THIS inline preview, not
  // just the full Views-tab renderer. Absent ⇒ `runLayoutWithIntent` collapses
  // to the byte-identical no-intent baseline (older/legacy views unchanged).
  // `?? undefined` mirrors ViewRenderer's `layoutIntentOf`: `applyLayoutPostpass`
  // guards on `=== undefined`, so a stray `layoutIntent: null` (a hand-edited or
  // non-schema-validated layout.json) must be normalised to undefined, not passed
  // through as null (which would deref `null.pins`).
  const intent: LayoutIntent | undefined = layout.layoutIntent ?? undefined;

  const initial = useMemo(() => buildReactFlowGraph(layout), [layout]);
  const [state, setState] = useState<LoadState>(
    needsFirstRenderLayout(initial.nodes)
      ? { kind: 'laying-out' }
      : { kind: 'ready', nodes: initial.nodes, edges: initial.edges },
  );

  // In-place re-layout support (LV1-S6/LV3-S3): when the upstream layout
  // object changes identity AFTER mount — the card re-fetched `layout.json`
  // because an "adjust layout" request re-emitted `kb:viewRendered` for this
  // same view — re-enter the laying-out state so ELK + post-pass re-run under
  // the NEW `layoutIntent`. Without this the state latches at 'ready' with the
  // old geometry and the card keeps showing the pre-adjustment arrangement
  // (the useState initializer only runs on mount). Ref-guarded so the mount
  // render itself never double-fires the pipeline.
  const layoutIdentityRef = useRef(layout);
  useEffect(() => {
    if (layoutIdentityRef.current === layout) return;
    layoutIdentityRef.current = layout;
    setState(
      needsFirstRenderLayout(initial.nodes)
        ? { kind: 'laying-out' }
        : { kind: 'ready', nodes: initial.nodes, edges: initial.edges },
    );
  }, [layout, initial]);

  // Run ELK layout exactly once when the graph needs positions. Re-run
  // if the upstream layout reference identity changes (a fresh card).
  useEffect(() => {
    if (state.kind !== 'laying-out') return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const laid = await runLayoutWithIntent(
          initial.nodes,
          initial.edges,
          intent,
        );
        if (cancelled) return;
        setState({ kind: 'ready', nodes: laid.nodes, edges: laid.edges });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        // Fall back to flat layout — better than nothing.
        setState({ kind: 'error', message: `Layout failed: ${msg}` });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state.kind, initial.nodes, initial.edges, intent]);

  if (state.kind === 'laying-out') {
    return (
      <div
        role="status"
        aria-label="Laying out view…"
        data-testid="mini-reactflow-laying-out"
        className="bg-cream-200 border-taupe-300 animate-pulse rounded-none border"
        style={{ width, height }}
      />
    );
  }

  if (state.kind === 'error') {
    return (
      <div
        role="alert"
        data-testid="mini-reactflow-error"
        className="text-sm text-accent-red"
        style={{ width, height, padding: 8 }}
      >
        {state.message}
      </div>
    );
  }

  return (
    <div
      data-testid="mini-reactflow-preview"
      style={{ width, height }}
      // tessa-view-renderer scopes the CSS variables (--tessa-bg,
      // --tessa-border, --tessa-edge, …) AND the `.port-label { position:
      // absolute }` rule that PartNode's port rows rely on. Without it the
      // port labels collapse into a single concatenated text run, the
      // block borders/backgrounds use undefined variables (transparent),
      // and edges render with no stroke colour.
      className="tessa-view-renderer border-taupe-300 overflow-hidden rounded-none border"
    >
      <ReactFlowProvider>
        <MiniReactFlowInner
          nodes={state.nodes}
          edges={state.edges}
          intent={intent}
          onRefit={(nodes, edges): void =>
            setState({ kind: 'ready', nodes, edges })
          }
        />
      </ReactFlowProvider>
      <EdgeLegend edges={state.edges} position="bottom-right" />
    </div>
  );
}

interface MiniReactFlowInnerProps {
  nodes: Node[];
  edges: Edge[];
  /** Persisted layout intent applied on the post-measurement re-layout. */
  intent: LayoutIntent | undefined;
  onRefit: (nodes: Node[], edges: Edge[]) => void;
}

/**
 * Renders the ReactFlow canvas and, once React Flow has measured the actual
 * DOM dimensions of every node (`useNodesInitialized` flips to true), runs
 * ELK a second time so edge anchors align with the measured handle
 * positions. Without this pass, edges drawn with `elkBendPoints` from the
 * pre-measurement layout exhibit visible offsets — the first-pass nominal
 * dimensions (`LEAF_W`, computed `leafHeight`) don't quite match what the
 * browser actually renders, so the polyline interpolation in
 * `<ConnectionEdge>` produces kinks and edges that look like they pass
 * through neighbouring nodes.
 */
function MiniReactFlowInner(props: MiniReactFlowInnerProps): JSX.Element {
  const { nodes, edges, intent, onRefit } = props;
  const nodesInitialized = useNodesInitialized();
  const reactFlowApi = useReactFlow();
  const refitDoneRef = useRef(false);

  useEffect(() => {
    if (!nodesInitialized || refitDoneRef.current) return undefined;
    refitDoneRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const laid = await runLayoutWithIntent(nodes, edges, intent);
        if (cancelled) return;
        onRefit(laid.nodes, laid.edges);
      } catch {
        // Keep the first-pass layout on failure.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nodesInitialized, nodes, edges, intent, onRefit]);

  // Re-fit the viewport once the post-measurement re-layout lands. The
  // `fitView` PROP frames only the mount's first paint — the pre-measurement
  // geometry — and React Flow never re-fits on its own when the measured ELK
  // pass above widens the arrangement, so outer nodes could end up past the
  // canvas edge with their connectors running off-screen (observed live: a
  // 4-node context view whose 4th box lay beyond the card's right border).
  // Mirrors the imperative post-ELK fit in `ViewRenderer` (its
  // "Auto-fit ONCE on open" effect). The first-pass `nodes` identity is
  // skipped — the prop already framed it; any later identity is a re-layout
  // whose geometry the viewport hasn't seen. Latched inside the rAF callback
  // (not before scheduling) so a StrictMode setup→cleanup→setup cycle cannot
  // cancel the only scheduled fit and leave the latch stuck true.
  const firstPassNodesRef = useRef(nodes);
  const refitViewportDoneRef = useRef(false);
  useEffect(() => {
    if (nodes === firstPassNodesRef.current) return undefined;
    if (refitViewportDoneRef.current) return undefined;
    const fit = (): void => {
      refitViewportDoneRef.current = true;
      void reactFlowApi.fitView({ padding: 0.15 });
    };
    // Defer one frame so React Flow has committed the re-laid nodes into its
    // store before we read their bounding box.
    if (typeof requestAnimationFrame !== 'function') {
      fit();
      return undefined;
    }
    const raf = requestAnimationFrame(fit);
    return () => cancelAnimationFrame(raf);
  }, [nodes, reactFlowApi]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={NODE_TYPES}
      edgeTypes={EDGE_TYPES}
      fitView
      fitViewOptions={{ padding: 0.15 }}
      defaultEdgeOptions={{ type: 'connection' }}
      // Read-only inside the chat card — interactivity (zoom, pan, drag,
      // save) lives in the expand-modal lightbox via `<ViewRenderer>` and
      // in the full Views-tab editor.
      panOnDrag={false}
      panOnScroll={false}
      zoomOnScroll={false}
      zoomOnPinch={false}
      zoomOnDoubleClick={false}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      preventScrolling={false}
      proOptions={{ hideAttribution: true }}
    >
      <Background gap={18} size={1} color="var(--tessa-canvas-dot)" />
    </ReactFlow>
  );
}
